import * as React from 'react';
import {
  Filter,
  Operators,
  TextFilter,
  NumericFilter,
  BooleanFilter,
  DateFilter,
  EnumFilter,
} from '@progress/kendo-react-data-tools';
import { AccentButton, MoreButton } from '../AccentButton';
import { AccentSpinner } from '../AccentSpinner';
import { t, A, showDialog } from '../../services/HelperService';
import { getWorkflowMetadata } from './workflowApi';
import { buildWorkflowMetadataTemplateCatalog } from './templates/buildWorkflowTemplateCatalog';
import { queryNoTracking, throwIfFrameworkException } from '../../services/DataService';
import { getUser } from '../../services/UserService';
import {
  REPORT_DATE_FROM_TOKEN,
  REPORT_DATE_TO_TOKEN,
  ReportDateRangePicker,
  computeReportDateRange,
  usesReportDateRange,
} from './ReportDateRangePicker';
import { gridSettingsManager } from '../grids/GridDataManager';
import { advancedFilterCache } from '../advancedfilter/AdvancedFilterConstants';
import { RTLGridQuickFilterSelector } from '../grids/RTLGridQuickFilterBar';
import { GridFilterView } from '../advancedfilter/GridFilterView';
import { patchCustomizationPrefs, resolveInitialDateRange } from '../customizations/customizationUserPrefs';

const q_TestQuery = queryNoTracking('InsyteWebApi_TestQuery', 0, true);
const q_StartGridExport = queryNoTracking('InsyteWebApi_StartGridExport', 0, true);

const safeString = (v) => (v ?? '').toString();

/** Rows fetched per grid page; every page fetch carries this as gridState.take. */
export const GRID_PAGE_SIZE = 50;

/** Rows a child-query fetch is bounded to (one on-demand fetch per expansion). */
export const CHILD_GRID_MAX_ROWS = 500;

/**
 * The entities a column link may target, mapped to their view routes. Mirrors
 * DataGridSessionProfile.SupportedLinkEntities (C#) — the server rejects definitions
 * declaring anything outside this set, so the two lists must move together.
 * (Invoice lines have no page of their own; the AI links them to their Invoice.)
 */
export const LINK_ENTITY_ROUTES = {
  job: '/Job/',
  company: '/Company/',
  contact: '/Contact/',
  activity: '/Activity/',
  invoice: '/Invoice/',
  supplyjob: '/SupplyJob/',
  opportunity: '/Opportunity/',
  payment: '/Payment/',
  jobline: '/JobLine/',
  supplyjobline: '/SupplyJobLine/',
};

/** The href for a link declaration and an id value, or null when unresolvable. */
export const linkHref = (entity, id) => {
  const route = LINK_ENTITY_ROUTES[safeString(entity).trim().toLowerCase()];
  if (!route || id == null || safeString(id).trim() === '') return null;
  return `${route}${id}`;
};

/** Reads a dot-path ('Contact.LastName') out of a row object. */
const getPath = (row, path) => safeString(path).split('.').reduce(
  (value, segment) => (value != null && typeof value === 'object' ? value[segment] : undefined),
  row
);

/**
 * The query's own top-level TOP when present — the grid's SEMANTIC total-row cap.
 * Client-side convenience only (the pager caption and take clamping); the server
 * parses the statement properly.
 */
export const parseSemanticTop = (query) => {
  const match = /\bSELECT\s+(?:DISTINCT\s+)?TOP\s*\(?\s*(\d+)/i.exec(safeString(query));
  return match ? Number(match[1]) : null;
};

/**
 * Composes one page fetch's structured grid state. The authored SQL travels VERBATIM —
 * the server wraps it and applies the viewer's paging/sort/filter around it (mirrors
 * SqlGridState (C#)): sort is by result COLUMN name, the filter tree narrows the
 * authored rows (it can never widen them), and includeTotalCount asks for the wrapped
 * COUNT that feeds the pager.
 */
export const buildGridState = ({ skip, take, sort, filter }) => ({
  skip: skip > 0 ? skip : 0,
  take,
  sort: sort ? [{ field: sort.field, dir: sort.dir }] : [],
  filter: filter || null,
  includeTotalCount: true,
});

// ── Viewer filtering (KendoReact Filter serialized to the structured SqlGridFilter) ──

/** The Filter component's empty state: one and-group with no conditions. */
export const EMPTY_FILTER = { logic: 'and', filters: [] };

const UNARY_FILTER_OPERATORS = ['isnull', 'isnotnull', 'isempty', 'isnotempty'];

/**
 * Drops half-built conditions (a value-less 'eq' the user is still typing) and empty
 * groups, so serialization only ever sees complete expressions — the serializer
 * throws on empties. ignoreCase is forced off on every condition: the API compares
 * strings case-insensitively natively, so Kendo's tolower() wrapping is pure noise
 * the parser would have to support.
 */
export const pruneFilter = (node) => {
  if (!node || typeof node !== 'object') return null;
  if (Array.isArray(node.filters)) {
    const filters = node.filters.map(pruneFilter).filter(Boolean);
    return filters.length > 0 ? { logic: node.logic === 'or' ? 'or' : 'and', filters } : null;
  }
  if (!safeString(node.field).trim() || !safeString(node.operator).trim()) return null;
  if (UNARY_FILTER_OPERATORS.includes(node.operator)) return { ...node, ignoreCase: false };
  if (node.value == null || (typeof node.value === 'string' && node.value.trim() === '')) return null;
  return { ...node, ignoreCase: false };
};

/**
 * Maps a (pruned) Kendo Filter descriptor to the server's SqlGridFilter tree, or null
 * when it holds no complete conditions. Kendo's operator names are the server's
 * operator names (eq/neq/gt/gte/lt/lte/contains/doesnotcontain/startswith/endswith/
 * isnull/isnotnull/isempty/isnotempty), so the mapping is shape-only: groups keep
 * { logic, filters }, conditions keep { field, operator, value } with Date values
 * serialized to ISO (the server binds them as parameters).
 */
export const filterToGridFilter = (filter) => {
  const pruned = pruneFilter(filter);
  if (!pruned) return null;
  const map = (node) => {
    if (Array.isArray(node.filters)) {
      return { logic: node.logic === 'or' ? 'or' : 'and', filters: node.filters.map(map) };
    }
    const value = node.value instanceof Date ? node.value.toISOString() : (node.value ?? null);
    return { field: safeString(node.field).trim(), operator: safeString(node.operator).trim(), value };
  };
  return map(pruned);
};

/** How many complete conditions a (pruned) filter tree holds. */
export const countFilterConditions = (node) => {
  if (!node || typeof node !== 'object') return 0;
  if (Array.isArray(node.filters)) return node.filters.reduce((sum, f) => sum + countFilterConditions(f), 0);
  return 1;
};

// Every Kendo text operator maps to a SQL predicate the server composes (LIKE with
// escaped wildcards), including 'doesnotcontain'.
const TEXT_FILTER_OPERATORS = Operators.text;

/**
 * Flattens the workflow metadata template catalog's FixedPicklists tree
 * (FixedPicklists -> entity -> field -> code leaves) into flat entries:
 * { entity, field, values: [{ value: code, label }] }. This is the FULL picklist
 * vocabulary — the page's rows only ever sample a subset of the codes.
 */
export const flattenPicklistVocabulary = (catalog) => {
  const entries = [];
  (Array.isArray(catalog) ? catalog : []).forEach((top) => {
    if (top?.group !== 'FixedPicklists') return;
    (top.items ?? []).forEach((entityNode) => {
      (entityNode?.items ?? []).forEach((fieldNode) => {
        const values = (fieldNode?.items ?? [])
          .filter((leaf) => !Array.isArray(leaf?.items))
          .map((leaf) => ({ value: safeString(leaf?.token).trim(), label: safeString(leaf?.label).trim() }))
          .filter((v) => v.value);
        if (values.length > 0) {
          entries.push({ entity: safeString(entityNode?.label).trim(), field: safeString(fieldNode?.label).trim(), values });
        }
      });
    });
  });
  return entries;
};

/** The dropdown text for a picklist code: t() first (matches the cells), catalog label fallback. */
const picklistOptionText = (code, label) => {
  const translated = safeString(t(code));
  return translated && translated !== code ? translated : (label || code);
};

/**
 * The full option list for a picklist column, as { value: code, text: label }.
 * The vocabulary keys fields per ENTITY but the column only knows its field PATH, so
 * candidates match on the path's last segment and a code sampled from the page rows
 * picks the right entity when several share the name (codes are family-prefixed —
 * status_job_* vs status_supply_job_* — so one sample settles it). With no sample the
 * candidates' values are unioned (still usable thanks to the prefixes), and sampled
 * codes missing from the vocabulary are kept defensively.
 */
export const resolvePicklistOptions = (column, rows, vocabulary) => {
  const sampled = [...new Set(
    rows.map((row) => getPath(row, column.field)).filter((v) => typeof v === 'string' && v.trim() !== '')
  )];
  const fieldName = safeString(column.field).split('.').pop().trim().toLowerCase();
  let candidates = (vocabulary ?? []).filter((e) => e.field.toLowerCase() === fieldName);
  if (sampled.length > 0) {
    const bySample = candidates.filter((e) => e.values.some((v) => sampled.includes(v.value)));
    if (bySample.length > 0) candidates = bySample;
  }
  const byCode = new Map();
  candidates.forEach((e) => e.values.forEach((v) => { if (!byCode.has(v.value)) byCode.set(v.value, v.label); }));
  sampled.forEach((code) => { if (!byCode.has(code)) byCode.set(code, ''); });
  return [...byCode.entries()]
    .map(([value, label]) => ({ value, text: picklistOptionText(value, label) }))
    .sort((a, b) => a.text.localeCompare(b.text));
};

/** The filter editor type for a column, sampled from the page's rows. */
const columnFilterType = (column, rows) => {
  if (column.picklist) return 'picklist';
  const sample = rows.map((row) => getPath(row, column.field)).find((v) => v != null);
  if (typeof sample === 'number') return 'number';
  if (typeof sample === 'boolean') return 'boolean';
  if (typeof sample === 'string' && ISO_DATE_REGEX.test(sample)) return 'date';
  return 'text';
};

/**
 * Per-column filter metadata shared by the quick-filter row and the advanced builder:
 * { [field]: { type, options } } where options is the full picklist option list.
 */
const buildColumnFilterMeta = (columns, rows, vocabulary) => {
  const meta = {};
  columns.forEach((column) => {
    const type = columnFilterType(column, rows);
    meta[column.field] = {
      type,
      options: type === 'picklist' ? resolvePicklistOptions(column, rows, vocabulary) : null,
    };
  });
  return meta;
};

/**
 * One Filter field per displayed column from the shared column meta: each type gets
 * its native editor/operators; picklist columns get a dropdown of the FULL vocabulary
 * with translated labels (the descriptor stores the raw code — that is what the data
 * holds). The enum editor is bound per field because the Filter's expression row
 * forwards only filter/onFilterChange/min/max to the editor, not a data prop.
 */
const buildFilterFields = (columns, meta) => columns.map((column) => {
  const base = { name: column.field, label: column.title };
  const m = meta?.[column.field] ?? { type: 'text', options: null };

  if (m.type === 'picklist' && (m.options?.length ?? 0) > 0) {
    const data = m.options.map((o) => ({ text: o.text, value: o.value }));
    const PicklistEditor = (props) => <EnumFilter {...props} data={data} />;
    return { ...base, filter: PicklistEditor, operators: Operators.boolean /* eq / neq */ };
  }
  if (m.type === 'number') return { ...base, filter: NumericFilter, operators: Operators.numeric };
  if (m.type === 'boolean') return { ...base, filter: BooleanFilter, operators: Operators.boolean };
  if (m.type === 'date') return { ...base, filter: DateFilter, operators: Operators.date };
  return { ...base, filter: TextFilter, operators: TEXT_FILTER_OPERATORS };
});

/**
 * The header quick-filter stubs' single values become one descriptor per column with
 * the type's natural operator: text searches (contains), everything else matches
 * exactly (eq). Returns null while the value is empty/unparsable — no condition.
 */
export const quickFilterDescriptor = (field, rawValue, type) => {
  const value = safeString(rawValue).trim();
  if (!value) return null;
  if (type === 'picklist') return { field, operator: 'eq', value, ignoreCase: false };
  if (type === 'boolean') return { field, operator: 'eq', value: value === 'true' };
  if (type === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? { field, operator: 'eq', value: n } : null;
  }
  return { field, operator: 'contains', value, ignoreCase: false };
};

const FunnelIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" style={{ flexShrink: 0 }} aria-hidden="true">
    <path d="M1 1h10L7.5 6v4.2L4.5 11V6L1 1z" fill="currentColor" />
  </svg>
);

/** The stub's one-line summary of an active quick filter. */
const quickFilterSummary = (value, meta) => {
  const raw = safeString(value).trim();
  if (!raw) return '';
  if (meta?.type === 'picklist') {
    return meta.options?.find((o) => o.value === raw)?.text ?? t(raw);
  }
  if (meta?.type === 'boolean') return raw === 'true' ? t('Yes') : t('No');
  return raw;
};

const QUICK_OVERLAY_WIDTH = 260;

/**
 * The expanded ("zoomed") editor for one column's quick filter: columns are often too
 * narrow to type into, so the focused editor floats ABOVE the table at a comfortable
 * fixed width, anchored to its filter cell (absolute within the scroll content, so it
 * scrolls with its column). Native input/select editors keep dismissal simple — no
 * portalled popups to special-case in the outside-click handler. Escape/Enter close;
 * the filter itself applies through the normal debounce while typing.
 */
const QuickFilterOverlay = React.forwardRef(({ column, meta, value, position, onChange, onClose }, ref) => {
  const inputRef = React.useRef(null);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
    inputRef.current?.focus();
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape' || e.key === 'Enter') {
      e.stopPropagation();
      onClose();
    }
  };

  const editorStyle = { width: '100%', fontSize: 12, padding: '4px 6px', border: '1px solid #ced4da', borderRadius: 4, boxSizing: 'border-box' };
  const type = meta?.type ?? 'text';

  return (
    <div
      ref={ref}
      data-tagname="quickFilterOverlay"
      onKeyDown={onKeyDown}
      style={{
        position: 'absolute',
        left: position.left,
        top: position.top,
        width: QUICK_OVERLAY_WIDTH,
        zIndex: 5,
        background: '#fff',
        border: '1px solid #ced4da',
        borderRadius: 6,
        boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
        padding: 8,
        transformOrigin: 'top left',
        transform: mounted ? 'scale(1)' : 'scale(0.85)',
        opacity: mounted ? 1 : 0,
        transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
        <span style={{ flex: 1, fontSize: 11, fontWeight: 600, opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {column.title}
        </span>
        {!!safeString(value).trim() && (
          <button
            type="button"
            style={{ border: 'none', background: 'none', fontSize: 11, color: '#6f42c1', cursor: 'pointer', padding: 0 }}
            onClick={() => { onChange(''); inputRef.current?.focus(); }}
          >
            {t('Clear')}
          </button>
        )}
      </div>
      {type === 'picklist' && (
        <select ref={inputRef} style={editorStyle} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t('(any)')}</option>
          {(meta.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.text}</option>
          ))}
        </select>
      )}
      {type === 'boolean' && (
        <select ref={inputRef} style={editorStyle} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t('(any)')}</option>
          <option value="true">{t('Yes')}</option>
          <option value="false">{t('No')}</option>
        </select>
      )}
      {type === 'number' && (
        <input ref={inputRef} type="number" style={editorStyle} value={value} placeholder={t('equals...')} onChange={(e) => onChange(e.target.value)} />
      )}
      {type === 'text' && (
        <input ref={inputRef} type="text" style={editorStyle} value={value} placeholder={t('contains...')} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
});

/**
 * Style keys a definition may declare. Mirrors DataGridSessionProfile.AllowedStyleKeys
 * (C#): the server rejects anything else, and this filter guarantees nothing else ever
 * reaches a React style prop even if a stale definition slips through.
 */
const ALLOWED_STYLE_KEYS = ['color', 'backgroundColor', 'background', 'fontWeight', 'fontStyle', 'textDecoration'];

const sanitizeStyle = (style) => {
  if (!style || typeof style !== 'object' || Array.isArray(style)) return undefined;
  const clean = {};
  for (const key of ALLOWED_STYLE_KEYS) {
    const value = style[key];
    if (typeof value === 'string' || typeof value === 'number') clean[key] = value;
  }
  return Object.keys(clean).length > 0 ? clean : undefined;
};

const normalizeLink = (link) => {
  if (!link || typeof link !== 'object') return undefined;
  const entity = safeString(link.entity).trim();
  const idField = safeString(link.idField).trim();
  return entity && idField ? { entity, idField } : undefined;
};

const CELL_STYLE_OPS = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'contains', 'empty', 'notEmpty'];

const normalizeCellStyles = (list) => {
  const rules = (Array.isArray(list) ? list : [])
    .map((rule) => {
      if (!rule || typeof rule !== 'object') return null;
      const when = rule.when && typeof rule.when === 'object' ? rule.when : null;
      const field = safeString(when?.field).trim();
      const op = safeString(when?.op).trim();
      const style = sanitizeStyle(rule.style);
      if (!field || !CELL_STYLE_OPS.includes(op) || !style) return null;
      return { when: { field, op, value: when.value }, style };
    })
    .filter(Boolean);
  return rules.length > 0 ? rules : undefined;
};

/**
 * Footer aggregates a CHILD grid column may declare. Child grids only: their rows
 * are one fully-loaded fetch, so a client-side total is complete — the main grid is
 * server-paged and a page-local total would lie.
 */
const TOTAL_OPS = ['sum', 'avg', 'min', 'max', 'count'];

/** Row-height bounds (px) a definition may declare — clamped, mirrored server-side. */
const MIN_ROW_HEIGHT = 20;
const MAX_ROW_HEIGHT = 80;

/**
 * Grid-level ROW options (declared beside 'columns', on the main grid and on each
 * child grid): rowStyle applies to every row, altRowStyle layers over odd rows (zebra
 * striping), rowStyles are conditional per-row rules with the cellStyles shape, and
 * rowHeight is a clamped pixel height.
 */
const normalizeRowOptions = (obj) => ({
  rowStyle: sanitizeStyle(obj.rowStyle),
  altRowStyle: sanitizeStyle(obj.altRowStyle),
  rowStyles: normalizeCellStyles(obj.rowStyles),
  rowHeight: typeof obj.rowHeight === 'number' && Number.isFinite(obj.rowHeight) && obj.rowHeight > 0
    ? Math.min(MAX_ROW_HEIGHT, Math.max(MIN_ROW_HEIGHT, Math.round(obj.rowHeight)))
    : undefined,
});

/** Parses the 'grid' output's evaluated value into { columns, childGrids } (or null). */
export const parseGridDefinition = (valueJson) => {
  const raw = safeString(valueJson).trim();
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  const normalizeColumns = (list) => (Array.isArray(list) ? list : [])
    .filter((c) => c && typeof c === 'object' && safeString(c.field).trim())
    .map((c) => ({
      field: safeString(c.field).trim(),
      title: safeString(c.title).trim() || safeString(c.field).trim(),
      sortable: c.sortable !== false,
      width: typeof c.width === 'number' && c.width > 0 ? c.width : undefined,
      picklist: c.picklist === true,
      align: ['left', 'center', 'right'].includes(safeString(c.align).trim()) ? safeString(c.align).trim() : undefined,
      style: sanitizeStyle(c.style),
      cellStyles: normalizeCellStyles(c.cellStyles),
      link: normalizeLink(c.link),
      total: TOTAL_OPS.includes(safeString(c.total).trim()) ? safeString(c.total).trim() : undefined,
    }));

  // Child grids NEST (contacts -> jobs -> job lines): each entry may carry its own
  // childGrids array. The depth guard only exists to stop a pathological definition;
  // the server caps the TOTAL child-grid count.
  const normalizeChildGrids = (list, depth) => {
    if (depth > 5) return [];
    return (Array.isArray(list) ? list : [])
      .filter((g) => g && typeof g === 'object' && (safeString(g.collection).trim() || safeString(g.queryName).trim()))
      .map((g) => ({
        collection: safeString(g.collection).trim() || undefined,
        queryName: safeString(g.queryName).trim() || undefined,
        title: safeString(g.title).trim() || safeString(g.collection).trim() || safeString(g.queryName).trim(),
        columns: normalizeColumns(g.columns),
        childGrids: normalizeChildGrids(g.childGrids, depth + 1),
        autoTotals: g.autoTotals === true,
        ...normalizeRowOptions(g),
      }))
      .filter((g) => g.columns.length > 0);
  };

  const columns = normalizeColumns(parsed.columns);
  const childGrids = normalizeChildGrids(parsed.childGrids, 1);

  return columns.length > 0 ? { columns, childGrids, ...normalizeRowOptions(parsed) } : null;
};

/**
 * Fallback definition inferred from the fetched rows, for the moment before the AI has
 * built the 'grid' output: scalar-valued keys become columns; array-of-object keys
 * become child grids with their own inferred columns.
 */
const inferDefinition = (rows) => {
  const columns = [];
  const childGrids = [];
  const seen = new Set();
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    for (const key of Object.keys(row)) {
      if (seen.has(key) || key.startsWith('@')) continue;
      seen.add(key);
      const value = row[key];
      if (Array.isArray(value)) {
        const childRows = value.filter((v) => v && typeof v === 'object' && !Array.isArray(v));
        const childColumns = [];
        const childSeen = new Set();
        for (const childRow of childRows) {
          for (const childKey of Object.keys(childRow)) {
            if (childSeen.has(childKey) || childKey.startsWith('@')) continue;
            childSeen.add(childKey);
            const childValue = childRow[childKey];
            if (childValue == null || typeof childValue !== 'object') {
              childColumns.push({ field: childKey, title: childKey, sortable: true });
            }
          }
        }
        if (childColumns.length > 0) childGrids.push({ collection: key, title: key, columns: childColumns });
      } else if (value == null || typeof value !== 'object') {
        columns.push({ field: key, title: key, sortable: true });
      }
    }
  }
  return columns.length > 0 ? { columns, childGrids } : null;
};

/** Formats a cell for display (numbers/booleans/ISO dates get locale treatment). */
const formatCell = (value, locale) => {
  if (value == null) return '';
  if (typeof value === 'number') return value.toLocaleString(locale);
  if (typeof value === 'boolean') return value ? t('Yes') : t('No');
  if (typeof value === 'object') return '';
  const text = safeString(value);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text)) {
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString(locale);
  }
  return text;
};

/** True when a conditional-style rule's `when` matches the row. */
const matchesCellStyleRule = (row, when) => {
  const value = getPath(row, when.field);
  const text = safeString(value);
  switch (when.op) {
    case 'empty': return value == null || text === '';
    case 'notEmpty': return value != null && text !== '';
    case 'eq': return text === safeString(when.value) || (Number.isFinite(Number(value)) && Number(value) === Number(when.value) && text !== '' && safeString(when.value) !== '');
    case 'ne': return !matchesCellStyleRule(row, { ...when, op: 'eq' });
    case 'contains': return text.toLowerCase().includes(safeString(when.value).toLowerCase());
    case 'lt': case 'lte': case 'gt': case 'gte': {
      const a = Number(value);
      const b = Number(when.value);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
      if (when.op === 'lt') return a < b;
      if (when.op === 'lte') return a <= b;
      if (when.op === 'gt') return a > b;
      return a >= b;
    }
    default: return false;
  }
};

/**
 * The declarative row-level style: rowStyle for every row, altRowStyle layered over
 * ODD rows (index is the row's ABSOLUTE position so stripes stay consistent across
 * pages), then any matching conditional rowStyles. Undefined when nothing applies —
 * the host's own row styling (selection tint) always composes AFTER this, so it wins.
 */
const rowStyleFor = (definition, row, index) => {
  let style;
  if (definition?.rowStyle) style = { ...definition.rowStyle };
  if (definition?.altRowStyle && index % 2 === 1) style = { ...style, ...definition.altRowStyle };
  if (definition?.rowStyles) {
    for (const rule of definition.rowStyles) {
      if (matchesCellStyleRule(row, rule.when)) style = { ...style, ...rule.style };
    }
  }
  return style;
};

/** The full inline style for one body cell: base + align + declared + conditional. */
const cellStyleFor = (row, column, baseStyle) => {
  let style = baseStyle;
  if (column.align || column.style) style = { ...style, ...(column.align ? { textAlign: column.align } : null), ...column.style };
  if (column.cellStyles) {
    for (const rule of column.cellStyles) {
      if (matchesCellStyleRule(row, rule.when)) style = { ...style, ...rule.style };
    }
  }
  return style;
};

/**
 * One cell's content: picklist columns translate the raw code through t()
 * ('status_job_open' -> 'Open' — the codes are top-level translation keys), and link
 * columns wrap the value in the app's <A> (same entity routes used everywhere, with
 * the open-in-new-tab context menu for free). The link needs BOTH a display value and
 * a resolvable id — a dead or empty link is worse than plain text.
 */
const renderCellContent = (row, column, locale) => {
  const raw = getPath(row, column.field);
  const isPicklistValue = column.picklist && typeof raw === 'string' && raw.trim() !== '';
  const display = isPicklistValue ? t(raw) : formatCell(raw, locale);
  if (!column.link || display === '') return display;

  const href = linkHref(column.link.entity, getPath(row, column.link.idField));
  return href ? <A href={href}>{display}</A> : display;
};

/**
 * The child grid's footer aggregates: explicit column totals first, then autoTotals
 * sums every remaining numeric column that isn't a link, a picklist, or ID-like.
 * Nulls are ignored (never coerced to zero); a column whose values are not numeric
 * yields no total rather than NaN. Returns null when nothing aggregates.
 */
const computeGridTotals = (definition, rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const totals = {};
  for (const column of definition.columns) {
    let op = column.total;
    if (!op && definition.autoTotals && !column.link && !column.picklist && !/id$/i.test(column.field)) {
      if (rows.some((r) => typeof getPath(r, column.field) === 'number')) op = 'sum';
    }
    if (!op) continue;
    if (op === 'count') {
      totals[column.field] = { op, value: rows.reduce((n, r) => n + (getPath(r, column.field) == null ? 0 : 1), 0) };
      continue;
    }
    const values = rows
      .map((r) => getPath(r, column.field))
      .filter((v) => typeof v === 'number' && Number.isFinite(v));
    if (values.length === 0) continue;
    const sum = values.reduce((a, b) => a + b, 0);
    totals[column.field] = {
      op,
      value: op === 'sum' ? sum
        : op === 'avg' ? sum / values.length
          : op === 'min' ? Math.min(...values)
            : Math.max(...values),
    };
  }
  return Object.keys(totals).length > 0 ? totals : null;
};

/** One footer total, labelled unless it is a plain sum (a bare count reads as a sum). */
const formatGridTotal = (entry, locale) => {
  const number = entry.value.toLocaleString(locale, { maximumFractionDigits: 2 });
  return entry.op === 'sum' ? number : `${t(entry.op)} ${number}`;
};

const totalCellStyle = {
  borderTop: '2px solid #dee2e6',
  background: '#fafbfc',
  padding: '3px 8px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

/** Generic value comparator for the client-side child-grid sort. */
const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return safeString(a).localeCompare(safeString(b), undefined, { numeric: true, sensitivity: 'base' });
};

const headerCellStyle = (sortable) => ({
  position: 'sticky',
  top: 0,
  background: '#f8f9fa',
  borderBottom: '2px solid #dee2e6',
  padding: '6px 10px',
  textAlign: 'left',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  cursor: sortable ? 'pointer' : 'default',
  userSelect: 'none',
});

const bodyCellStyle = {
  borderBottom: '1px solid #f1f3f5',
  padding: '4px 10px',
  whiteSpace: 'nowrap',
};

const SortIndicator = ({ dir }) => (
  dir ? <span style={{ marginLeft: 4, fontSize: 10 }}>{dir === 'asc' ? '\u25B2' : '\u25BC'}</span> : null
);

/**
 * Toolbar button matching the standard grids' look: white, thin border, a leading
 * material icon, and an uppercase label (uppercased via CSS so translations stay
 * untouched). 'active' marks a toggled-on state (the filter panel being open).
 */
const GridToolbarButton = ({ icon, label, active, disabled, onClick, tagName, title }) => (
  <button
    type="button"
    data-tagname={tagName}
    disabled={disabled}
    title={title}
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      border: active ? '1px solid #4c8dd6' : '1px solid #d0d5da',
      borderRadius: 3,
      background: active ? '#eef4fd' : '#fff',
      color: active ? '#2b6cb0' : '#3d4650',
      padding: '5px 12px',
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      lineHeight: '16px',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.55 : 1,
    }}
  >
    <span className="material-icons" style={{ fontSize: 16 }} aria-hidden>{icon}</span>
    {label}
  </button>
);

const PARENT_TOKEN_REGEX = /\{\{\s*parent\.([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*)\s*\}\}/g;

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * Renders a parent-row value as the RAW string the server binds as the token's SQL
 * parameter, mirroring ParentRowTokens.RenderSqlParameterValue (C#): strings and
 * guids verbatim (no quoting — parameters are never spliced into SQL text), numbers/
 * booleans invariant, ISO dates normalized to UTC ISO, null as the empty string (the
 * child query then matches nothing, the truthful result for "this row has no key").
 */
export const renderSqlParameterValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  const text = safeString(value);
  if (GUID_REGEX.test(text)) return text.toLowerCase();
  if (ISO_DATE_REGEX.test(text)) {
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().replace(/\.\d{3}Z$/, 'Z');
  }
  return text;
};

/**
 * The template values for one child-query fetch: every {{parent.<Field>}} token in the
 * query text, resolved against the expanded parent row. Keys are the FULL token text —
 * that is the server's substitution contract (the executor binds each as a parameter).
 */
export const buildParentTokenValues = (queryText, parentRow) => {
  const values = {};
  const text = safeString(queryText);
  let match;
  PARENT_TOKEN_REGEX.lastIndex = 0;
  while ((match = PARENT_TOKEN_REGEX.exec(text)) !== null) {
    values[match[0]] = renderSqlParameterValue(getPath(parentRow, match[1]));
  }
  return values;
};

/**
 * Tokens whose field path does not exist on the parent row at all (undefined, as
 * opposed to a present-but-null value): the definition and the parent query disagree,
 * and running the fetch would silently match nothing. Fail loudly instead.
 */
export const findUnresolvableParentTokens = (queryText, parentRow) => {
  const missing = [];
  const text = safeString(queryText);
  let match;
  PARENT_TOKEN_REGEX.lastIndex = 0;
  while ((match = PARENT_TOKEN_REGEX.exec(text)) !== null) {
    if (getPath(parentRow, match[1]) === undefined && !missing.includes(match[0])) missing.push(match[0]);
  }
  return missing;
};

/**
 * The wrapper around an expanded row's child grids: fills the parent grid's width
 * (a master-detail child grid spreads its columns across the row) while the detail
 * cell's left padding indents each nesting level so the hierarchy still reads.
 */
const ChildGridsBlock = ({ childGrids, parentRow, ctx }) => (
  <div style={{ background: '#fbfbfd', border: '1px solid #eef0f3', borderRadius: 6, padding: '2px 10px 4px 10px' }}>
    {childGrids.map((childGrid) => (
      childGrid.queryName ? (
        <ChildQueryGrid
          key={childGrid.queryName}
          definition={childGrid}
          parentRow={parentRow}
          ctx={ctx}
        />
      ) : (
        <ChildGridTable
          key={childGrid.collection}
          definition={childGrid}
          rows={(Array.isArray(getPath(parentRow, childGrid.collection)) ? getPath(parentRow, childGrid.collection) : [])}
          ctx={ctx}
        />
      )
    ))}
  </div>
);

/**
 * A child grid's nested table. Sorting is CLIENT-SIDE: a query-mode child's rows are
 * one bounded on-demand fetch already in memory. A child grid with its OWN childGrids renders
 * an expander per row, recursing into the next level — each row of THIS grid is the
 * parent row for the grids nested under it.
 */
const ChildGridTable = ({ definition, rows, ctx }) => {
  const [sort, setSort] = React.useState(null);
  const [expandedRows, setExpandedRows] = React.useState(() => new Set());

  const nestedGrids = definition.childGrids ?? [];
  const hasExpander = nestedGrids.length > 0 && rows.length > 0;

  // Totals aggregate the FULL row set (sorting never changes them).
  const totals = React.useMemo(() => computeGridTotals(definition, rows), [definition, rows]);

  const sorted = React.useMemo(() => {
    if (!sort) return rows;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => factor * compareValues(getPath(a, sort.field), getPath(b, sort.field)));
  }, [rows, sort]);

  const onHeaderClick = (column) => {
    // Expansion tracks row indexes; a re-sort shuffles them, so collapse.
    setExpandedRows(new Set());
    setSort((prev) => {
      if (prev?.field !== column.field) return { field: column.field, dir: 'asc' };
      return prev.dir === 'asc' ? { field: column.field, dir: 'desc' } : null;
    });
  };

  const toggleExpanded = (ix) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(ix)) next.delete(ix); else next.add(ix);
      return next;
    });
  };

  return (
    <div style={{ margin: '4px 0 8px 0' }}>
      <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.75, padding: '2px 0' }}>{definition.title}</div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 11, opacity: 0.6, padding: '2px 0 4px 0' }}>{t('No rows.')}</div>
      ) : (
        <table style={{ borderCollapse: 'collapse', fontSize: 11, background: '#fff', border: '1px solid #e9ecef', width: '100%' }}>
          <thead>
            <tr>
              {hasExpander && <th style={{ ...headerCellStyle(false), position: 'static', width: 24, padding: '4px 6px' }} />}
              {definition.columns.map((column) => (
                <th
                  key={column.field}
                  style={{ ...headerCellStyle(true), position: 'static', fontSize: 11, padding: '4px 8px' }}
                  onClick={() => onHeaderClick(column)}
                  title={column.field}
                >
                  {column.title}
                  <SortIndicator dir={sort?.field === column.field ? sort.dir : null} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, ix) => (
              // Preview rows have no identity; index keys are fine for a read-only table.
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={ix}>
                <tr style={{
                  ...(definition.rowHeight ? { height: definition.rowHeight } : null),
                  ...rowStyleFor(definition, row, ix),
                }}
                >
                  {hasExpander && (
                    <td style={{ ...bodyCellStyle, padding: '3px 6px', cursor: 'pointer', textAlign: 'center', width: 24 }} onClick={() => toggleExpanded(ix)}>
                      <span style={{ display: 'inline-block', transform: expandedRows.has(ix) ? 'rotate(90deg)' : 'none', transition: 'transform 0.1s', fontSize: 9, opacity: 0.7 }}>
                        {'\u25B6'}
                      </span>
                    </td>
                  )}
                  {definition.columns.map((column) => (
                    <td key={column.field} style={cellStyleFor(row, column, { ...bodyCellStyle, padding: '3px 8px' })}>
                      {renderCellContent(row, column, ctx.locale)}
                    </td>
                  ))}
                </tr>
                {hasExpander && expandedRows.has(ix) && (
                  <tr>
                    <td colSpan={definition.columns.length + 1} style={{ ...bodyCellStyle, padding: '2px 8px 6px 26px' }}>
                      <ChildGridsBlock childGrids={nestedGrids} parentRow={row} ctx={ctx} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          {totals && (
            <tfoot>
              <tr data-tagname="childGridTotals">
                {hasExpander && <td style={totalCellStyle} />}
                {definition.columns.map((column) => {
                  const entry = totals[column.field];
                  return (
                    <td key={column.field} style={{ ...totalCellStyle, textAlign: column.align ?? (entry ? 'right' : undefined) }}>
                      {entry ? formatGridTotal(entry, ctx.locale) : ''}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </div>
  );
};

/**
 * A query-backed child grid: fetched ON DEMAND when its parent row expands, with the
 * expanded row's values substituted into the child query's {{parent.<Field>}} tokens
 * (bound as SQL parameters server-side). This is what lets an AGGREGATE main grid
 * still expand into detail rows — SQL rows are flat, so the child query fetches by key.
 * Mounting is the fetch trigger (the grid only renders this once expanded);
 * re-expanding re-fetches, which keeps the child rows fresh for free. At nested
 * levels the tokens resolve against THIS grid's own parent row — the row of the grid
 * it sits directly inside.
 */
const ChildQueryGrid = ({ definition, parentRow, ctx }) => {
  const queryEntry = (ctx.queries ?? []).find(
    (q) => safeString(q.name).trim().toLowerCase() === safeString(definition.queryName).trim().toLowerCase()
  );
  const childQuery = safeString(queryEntry?.query).trim();

  const [fetchState, setFetchState] = React.useState({ loading: !!childQuery, rows: [], error: '' });

  React.useEffect(() => {
    if (!childQuery) {
      setFetchState({ loading: false, rows: [], error: t('The child query is missing.') });
      return undefined;
    }
    // A token the parent row cannot supply would fetch with 'null' substituted and
    // silently show an empty grid — surface the mismatch instead.
    const unresolvable = findUnresolvableParentTokens(childQuery, parentRow);
    if (unresolvable.length > 0) {
      setFetchState({
        loading: false,
        rows: [],
        error: `${t('The parent row has no value for')} ${unresolvable.join(', ')} — ${t('the parent query must return that field, or the child query must reference a field it returns.')}`,
      });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setFetchState({ loading: true, rows: [], error: '' });
      try {
        const res = throwIfFrameworkException(await q_TestQuery.getFirstOrDefault({
          query: childQuery,
          queryLanguage: 'sql',
          expectedResultShape: 'all',
          apiVersion: safeString(ctx.apiVersion) || 'v2',
          templateValues: { ...(ctx.templateValues ?? {}), ...buildParentTokenValues(childQuery, parentRow) },
          convertDatesToUserTimeZone: true,
          maxPreviewRows: CHILD_GRID_MAX_ROWS,
        }));
        if (cancelled) return;
        const ok = !!(res?.TestOk ?? res?.testOk);
        if (!ok) {
          setFetchState({ loading: false, rows: [], error: safeString(res?.Error ?? res?.error) || t('The query failed.') });
          return;
        }
        const result = res?.Result ?? res?.result;
        setFetchState({ loading: false, rows: Array.isArray(result) ? result : [], error: '' });
      } catch (err) {
        if (cancelled) return;
        setFetchState({ loading: false, rows: [], error: safeString(err?.message) || safeString(err) });
      }
    })();
    return () => { cancelled = true; };
    // The parent row is fixed for this mount (a new expansion is a new mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childQuery, ctx.apiVersion]);

  if (fetchState.loading) {
    return (
      <div style={{ margin: '4px 0 8px 0' }}>
        <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.75, padding: '2px 0' }}>{definition.title}</div>
        <div style={{ fontSize: 11, opacity: 0.6, padding: '2px 0 4px 0' }}>{t('Loading...')}</div>
      </div>
    );
  }
  if (fetchState.error) {
    return (
      <div style={{ margin: '4px 0 8px 0' }}>
        <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.75, padding: '2px 0' }}>{definition.title}</div>
        <div style={{ fontSize: 11, color: '#b71c1c', padding: '2px 0 4px 0' }}>{fetchState.error}</div>
      </div>
    );
  }
  return <ChildGridTable definition={definition} rows={fetchState.rows} ctx={ctx} />;
};

/**
 * The data-grid profile's preview: the query's rows rendered LIVE in a paged, sortable
 * HTML table driven by the 'grid' definition output. The authored SQL travels VERBATIM
 * on every fetch; the viewer's paging/sorting/filtering rides beside it as a
 * STRUCTURED gridState (mirrors SqlGridState (C#)) that the server composes around
 * the wrapped statement — clicking a header cycles that column asc → desc → back to
 * the query's own ORDER BY, the pager moves gridState.skip, and includeTotalCount
 * supplies the pager's total.
 *
 * The definition is SELF-DESCRIBING (see DataGridSessionProfile): picklist columns
 * translate their codes through t(), link columns render the app's <A> to the entity's
 * view route, and column/cell styles come from the declared (whitelisted) style keys.
 *
 * TOP on the authored query is SEMANTIC: it caps the TOTAL rows the grid pages
 * through ("top 50 jobs" is a 50-row grid); without one the grid browses every
 * matching row, one page at a time.
 *
 * Child grids appear per expanded row, each backed by its own session query: the
 * fetch substitutes the expanded row's values into the child query's
 * {{parent.<Field>}} tokens (bound as SQL parameters server-side). Child grids NEST —
 * a child grid with its own childGrids renders per-row expanders too, and each deeper
 * level's tokens resolve against the row of the grid it sits directly inside.
 *
 * When the query uses the {{report.dateFrom}}/{{report.dateTo}} tokens a date-range
 * bar appears (same contract as the report/export previews).
 *
 * FILTERING is two-tier. Each column HEADER carries an inline funnel stub (colored
 * pill with the value summary while active); clicking one ZOOMS into a fixed-width
 * overlay editor (columns are often too narrow to type into) with the type's natural
 * editor and operator — text contains, picklist/boolean/number equals. The Filter
 * button additionally opens a
 * KendoReact Filter builder for multi-condition and/or logic. Both feed ONE effective
 * descriptor that maps to the structured SqlGridFilter tree riding in gridState — the
 * server composes it as parameterized predicates that NARROW the authored query (it
 * can never widen what the artifact returns). Filter fields are result COLUMN names,
 * which is exactly what the grid's columns are. Picklist editors offer the FULL
 * translated vocabulary (from the cached workflow metadata), not just the codes the
 * current page happens to sample.
 */
/** Parses a saved custom quick filter payload into a builder-format tree, or null. */
const parseSavedQuickFilterTree = (json) => {
  try {
    const parsed = JSON.parse(safeString(json));
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Array.isArray(parsed.filters)) return parsed;
  } catch { /* malformed rows are simply not offered */ }
  return null;
};

// Per-SPA-session view state for SAVED grids (the standard grids' getLastPage
// pattern): revisiting a saved grid within the session restores its page position,
// sort and filters instantly. Durable preferences ride in the user's GridSettings
// row under the same stateKey.
const savedGridSessionState = new Map();

const quickFilterPillStyle = (selected) => ({
  border: selected ? '1px solid #6f42c1' : '1px solid #cbd5e1',
  borderRadius: 14,
  background: selected ? '#f3e8ff' : '#fff',
  color: selected ? '#6f42c1' : '#334155',
  padding: '2px 12px',
  fontSize: 12,
  fontWeight: selected ? 700 : 500,
  cursor: 'pointer',
});

export const DataGridPreview = ({ queries, outputs, busy, apiVersion, sessionOptions, templateValues, onTemplateValuesChange, onSessionOptionsChange, onRefresh, onDefect, designMode = true, stateKey, filterScopeKey, exportLabel }) => {
  const user = getUser();
  const locale = safeString(user?.Locale) || 'en-AU';
  const timeZone = safeString(user?.IANATimeZoneID) || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Saved quick filter DEFINITIONS are shared per grid (scopeKey), while the
  // SELECTION of pills — like all other viewer settings — is per instance
  // (stateKey): the same grid on two dashboard widgets shares one filter list
  // but each widget picks its own active pills.
  const scopeKey = filterScopeKey ?? stateKey;

  // In-session remembered view state for this saved grid (read once; initializers
  // below seed from it, and a write-through effect keeps the map current).
  const sessionStateRef = React.useRef(stateKey ? savedGridSessionState.get(stateKey) : null);
  const sessionState = sessionStateRef.current;

  // The PRIMARY query feeds the grid; other queries back queryName child grids.
  const gridQueryEntry = (queries ?? []).find((q) => safeString(q.name).trim().toLowerCase() === 'data' && safeString(q.query).trim())
    ?? (queries ?? []).find((q) => safeString(q.query).trim());
  const gridQuery = safeString(gridQueryEntry?.query).trim();
  const semanticTop = parseSemanticTop(gridQuery);

  const gridOutput = (outputs ?? []).find((o) => safeString(o.name).trim().toLowerCase() === 'grid');
  const declaredDefinition = parseGridDefinition(gridOutput?.valueJson);
  // The definition rides in the grid output's EVALUATED value. When a host opens a
  // saved customization the script exists but hasn't run yet (valueJson empty,
  // testOk null) — inferring columns from raw rows here would flash untitled,
  // untranslated columns, so the grid holds as "pending" until the run lands.
  // A FAILED run falls back to inferred columns but says so, instead of silently
  // rendering the wrong grid.
  const definitionPending = !declaredDefinition && !!safeString(gridOutput?.script).trim() && gridOutput?.testOk == null;
  const definitionError = !declaredDefinition && gridOutput?.testOk === false
    ? (safeString(gridOutput?.error).trim() || t('The grid definition failed to evaluate; showing raw columns.'))
    : '';

  // ── Grid state (skip/sort travel as the structured gridState) ──
  const [skip, setSkip] = React.useState(sessionState?.skip ?? 0);
  const [sort, setSort] = React.useState(sessionState?.sort ?? null); // { field, dir: 'asc'|'desc' } | null
  // Selected row as an ABSOLUTE row number (skip + index on the page): stays put while
  // paging away and back, and rides the session map so it survives navigation. Any
  // reorder/refilter invalidates it.
  const [selectedRow, setSelectedRow] = React.useState(sessionState?.selectedRow ?? null);
  const [fetchState, setFetchState] = React.useState({ loading: false, rows: [], total: null, error: '' });
  // Expansion is keyed by ABSOLUTE row position (skip + page index) so it survives
  // paging away and back; like the selected row it lives in the session map only
  // (memory), and resets whenever sort/filter/query change reshuffles positions.
  const [expandedRows, setExpandedRows] = React.useState(() => new Set(sessionState?.expandedRows ?? []));
  const [refreshTick, setRefreshTick] = React.useState(0);

  // ── Viewer filter: the quick-filter row (one value per column) and the advanced
  // Filter builder both feed ONE effective descriptor; its complete conditions apply
  // debounced as the structured filter tree riding in gridState. appliedFilter holds
  // that tree SERIALIZED (a stable string keeps the fetch effect's deps honest).
  const [filterDraft, setFilterDraft] = React.useState(sessionState?.filterDraft ?? EMPTY_FILTER);
  const [quickFilters, setQuickFilters] = React.useState(sessionState?.quickFilters ?? {}); // { [field]: rawValue }
  const [quickEditor, setQuickEditor] = React.useState(null); // { field, left, top } | null
  const [appliedFilter, setAppliedFilter] = React.useState(sessionState?.appliedFilter ?? '');
  const [showFilter, setShowFilter] = React.useState(false);
  const canFilter = !!gridQuery;

  // ── Saved-grid memory & shared quick filters (only when the host names a stateKey,
  // i.e. rendering a SAVED customization — the authoring dialog stays stateless) ──
  // Durable preferences reuse the standard grids' GridSettings row (key = stateKey);
  // shared quick filters reuse the GridFilter table and its selector/save screens,
  // with this grid's own filter-tree format stored under the "cg_" scope.
  const [settingsReady, setSettingsReady] = React.useState(!stateKey || !!sessionState);
  const [quickFilterSelection, setQuickFilterSelection] = React.useState(sessionState?.quickFilterSelection ?? []); // [{ ID, Selected }]
  const [savedFilterDefs, setSavedFilterDefs] = React.useState(null); // [{ ID, Name, tree }] | null while loading
  const [savedFilterEpoch, setSavedFilterEpoch] = React.useState(0);

  // One-time durable restore; skipped when in-session state already covers this visit.
  React.useEffect(() => {
    if (!stateKey || settingsReady) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const s = await gridSettingsManager.getSettings(stateKey);
        if (cancelled || !s) return;
        if (s.Sort?.field) setSort({ field: safeString(s.Sort.field), dir: s.Sort.dir === 'desc' ? 'desc' : 'asc' });
        if (s.FilterDraft && typeof s.FilterDraft === 'object' && Array.isArray(s.FilterDraft.filters)) setFilterDraft(s.FilterDraft);
        if (s.QuickRow && typeof s.QuickRow === 'object') setQuickFilters(s.QuickRow);
        if (typeof s.AppliedFilter === 'string') setAppliedFilter(s.AppliedFilter);
        if (Array.isArray(s.QuickFilters)) setQuickFilterSelection(s.QuickFilters.filter((v) => v && v.ID != null).map((v) => ({ ID: v.ID, Selected: !!v.Selected })));
      } finally {
        if (!cancelled) setSettingsReady(true);
      }
    })();
    return () => { cancelled = true; };
    // Mount-time restore for one stateKey; settingsReady only flips forward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey]);

  // Durable persistence. filterDraft/quickFilters churn per keystroke, so they are
  // captured when appliedFilter SETTLES (the 600ms debounce below) instead of tracked.
  React.useEffect(() => {
    if (!stateKey || !settingsReady) return;
    gridSettingsManager.updateGridSettings(stateKey, {
      Sort: sort,
      FilterDraft: filterDraft,
      QuickRow: quickFilters,
      AppliedFilter: appliedFilter,
      QuickFilters: quickFilterSelection,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey, settingsReady, sort, appliedFilter, quickFilterSelection]);

  // The saved quick filters this user may use for this grid (owner/user/team access —
  // the same FilterByAccess rules as standard grids; the cache clears on save).
  // Fetched by the grid's SHARED scope, not the instance key.
  React.useEffect(() => {
    if (!scopeKey) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const rows = await advancedFilterCache.getFilters(scopeKey);
        if (cancelled) return;
        setSavedFilterDefs((rows ?? [])
          .map((r) => ({ ID: r.ID, Name: safeString(r.Name), tree: parseSavedQuickFilterTree(r.Filter) }))
          .filter((r) => r.tree));
      } catch {
        if (!cancelled) setSavedFilterDefs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [scopeKey, savedFilterEpoch]);

  const selectedSavedFilterTrees = React.useMemo(() => {
    const selected = new Set((quickFilterSelection ?? []).filter((v) => v?.Selected).map((v) => v.ID));
    return (savedFilterDefs ?? []).filter((f) => selected.has(f.ID)).map((f) => f.tree);
  }, [quickFilterSelection, savedFilterDefs]);

  // The FULL picklist vocabulary (entity -> field -> codes) from the cached workflow
  // metadata: the page's rows only sample whatever codes happen to be visible, so
  // dropdowns would otherwise miss values. Failure falls back to sampled codes.
  const [picklistVocabulary, setPicklistVocabulary] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const metadata = await getWorkflowMetadata();
        const catalog = buildWorkflowMetadataTemplateCatalog(metadata);
        if (!cancelled) setPicklistVocabulary(flattenPicklistVocabulary(catalog));
      } catch {
        // Dropdowns degrade to the codes sampled from the page rows.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sticky per-column types: a filtered-out (empty) page has no values to sample, and
  // retyping a numeric column as text would flip its descriptor to an invalid
  // contains() — the last type learned from real rows wins until the query changes.
  const columnTypeCacheRef = React.useRef({});

  // Template values are a fetch input; a stable key keeps the effects honest.
  const templateKey = JSON.stringify(templateValues ?? {});

  // A different query (or date range) invalidates the current page position.
  // First-run guarded: the mount pass must not wipe restored session state.
  const skipResetMountRef = React.useRef(true);
  React.useEffect(() => {
    if (skipResetMountRef.current) { skipResetMountRef.current = false; return; }
    setSkip(0);
    setSelectedRow(null);
    setExpandedRows(new Set());
  }, [gridQuery, templateKey]);

  // A different query may drop the columns the filter references — start clean.
  const filterResetMountRef = React.useRef(true);
  React.useEffect(() => {
    if (filterResetMountRef.current) { filterResetMountRef.current = false; return; }
    setFilterDraft(EMPTY_FILTER);
    setQuickFilters({});
    setQuickEditor(null);
    setAppliedFilter('');
    columnTypeCacheRef.current = {};
  }, [gridQuery]);

  // ── Page fetch: one bounded request per page/sort/query change ──
  React.useEffect(() => {
    // Waits for the durable-settings restore so the FIRST fetch already carries the
    // remembered sort/filter (instead of fetching unfiltered and refetching).
    if (!settingsReady) return undefined;
    if (!gridQuery) {
      setFetchState({ loading: false, rows: [], total: null, error: '' });
      return undefined;
    }
    const take = semanticTop != null ? Math.max(0, Math.min(GRID_PAGE_SIZE, semanticTop - skip)) : GRID_PAGE_SIZE;
    if (take === 0) {
      setFetchState({ loading: false, rows: [], total: semanticTop, error: '' });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setFetchState((prev) => ({ ...prev, loading: true, error: '' }));
      try {
        const res = throwIfFrameworkException(await q_TestQuery.getFirstOrDefault({
          query: gridQuery,
          queryLanguage: 'sql',
          expectedResultShape: 'all',
          apiVersion: safeString(apiVersion) || 'v2',
          templateValues: { ...(templateValues ?? {}) },
          convertDatesToUserTimeZone: true,
          gridState: buildGridState({
            skip,
            take,
            sort,
            filter: canFilter && appliedFilter ? JSON.parse(appliedFilter) : null,
          }),
        }));
        if (cancelled) return;

        const ok = !!(res?.TestOk ?? res?.testOk);
        if (!ok) {
          setFetchState({ loading: false, rows: [], total: null, error: safeString(res?.Error ?? res?.error) || t('The query failed.') });
          return;
        }
        const result = res?.Result ?? res?.result;
        const rows = Array.isArray(result) ? result : [];
        const totalRaw = res?.TotalCount ?? res?.totalCount;
        const total = totalRaw != null && semanticTop != null ? Math.min(Number(totalRaw), semanticTop) : (totalRaw != null ? Number(totalRaw) : null);
        setFetchState({ loading: false, rows, total, error: '' });
      } catch (err) {
        if (cancelled) return;
        setFetchState({ loading: false, rows: [], total: null, error: safeString(err?.message) || safeString(err) });
      }
    })();
    return () => { cancelled = true; };
    // templateValues rides in via templateKey; semanticTop derives from gridQuery.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsReady, gridQuery, templateKey, skip, sort?.field, sort?.dir, appliedFilter, refreshTick, apiVersion]);

  const definition = declaredDefinition ?? (definitionPending ? null : inferDefinition(fetchState.rows));
  const columns = definition?.columns ?? [];
  const childGrids = definition?.childGrids ?? [];
  const hasExpander = childGrids.length > 0;

  // Per-column filter meta (type + full picklist options), memoized so field/editor
  // identities only change with the data, not on every keystroke re-render; columns
  // derive from the same inputs. Types learned from real rows stick (see the ref).
  const columnFilterMeta = React.useMemo(() => {
    const meta = buildColumnFilterMeta(columns, fetchState.rows, picklistVocabulary);
    Object.entries(meta).forEach(([field, m]) => {
      const cached = columnTypeCacheRef.current[field];
      const hasSample = fetchState.rows.some((row) => getPath(row, field) != null);
      if (!hasSample && cached) {
        m.type = cached.type;
        if (!(m.options?.length) && cached.options?.length) m.options = cached.options;
      } else {
        columnTypeCacheRef.current[field] = { type: m.type, options: m.options };
      }
    });
    return meta;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridOutput?.valueJson, fetchState.rows, picklistVocabulary]);

  const filterFields = React.useMemo(
    () => buildFilterFields(columns, columnFilterMeta),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnFilterMeta]
  );

  // Quick-row values, the builder draft AND any selected saved quick filters compose
  // into ONE effective filter; each rides as a sibling of the AND group.
  const effectiveFilter = React.useMemo(() => ({
    logic: 'and',
    filters: [
      ...Object.entries(quickFilters)
        .map(([field, value]) => quickFilterDescriptor(field, value, columnFilterMeta[field]?.type))
        .filter(Boolean),
      filterDraft,
      ...selectedSavedFilterTrees,
    ],
  }), [quickFilters, filterDraft, columnFilterMeta, selectedSavedFilterTrees]);

  const activeFilterCount = countFilterConditions(pruneFilter(effectiveFilter));

  // Debounced apply: the serialized tree only changes once a condition is complete
  // (pruneFilter drops half-typed ones in the builder; empty quick values produce
  // no descriptor), so this settles quickly while typing.
  React.useEffect(() => {
    const tree = filterToGridFilter(effectiveFilter);
    const serialized = tree ? JSON.stringify(tree) : '';
    if (serialized === appliedFilter) return undefined;
    const handle = setTimeout(() => {
      setSkip(0);
      setSelectedRow(null);
      setExpandedRows(new Set());
      setAppliedFilter(serialized);
    }, 600);
    return () => clearTimeout(handle);
    // appliedFilter is the settle target, not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveFilter]);

  // ── Quick-filter row interactions ──
  const quickCellRefs = React.useRef({});
  const quickOverlayRef = React.useRef(null);

  const setQuickFilterValue = (field, value) => {
    setQuickFilters((prev) => {
      const next = { ...prev };
      if (safeString(value).trim() === '') delete next[field]; else next[field] = value;
      return next;
    });
  };

  // The overlay anchors under its header cell in scroll-CONTENT coordinates (the
  // container is the positioning context and the table sits at its origin), clamped
  // so it never overhangs the table's right edge. It scrolls with its column free.
  const openQuickEditor = (field) => {
    if (quickEditor?.field === field) { setQuickEditor(null); return; }
    const cell = quickCellRefs.current[field];
    const table = cell?.closest('table');
    if (!cell || !table) return;
    const left = Math.max(0, Math.min(cell.offsetLeft, table.offsetWidth - QUICK_OVERLAY_WIDTH - 4));
    setQuickEditor({ field, left, top: cell.offsetTop + cell.offsetHeight + 2 });
  };

  // Mousedown outside the overlay collapses it. The header funnel stubs are treated
  // as "inside": their own onClick handles toggling/switching columns, and letting
  // this handler close first would make the same stub's click immediately reopen.
  React.useEffect(() => {
    if (!quickEditor) return undefined;
    const onDocMouseDown = (ev) => {
      if (quickOverlayRef.current?.contains(ev.target)) return;
      if (ev.target?.closest?.('[data-role="quickFilterStub"]')) return;
      setQuickEditor(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [quickEditor]);

  // Everything a child grid needs to render (and fetch) its own nested levels,
  // threaded through the recursion as one object.
  const childGridContext = { queries, apiVersion, templateValues, locale };

  // ── Viewer-controlled date range (same contract as the report/export previews) ──
  // Seeded from the in-session state, then the viewer's remembered choice, then the
  // author's saved default.
  const [dateRange, setDateRange] = React.useState(() => (
    sessionState?.dateRange ?? resolveInitialDateRange(stateKey, sessionOptions)
  ));
  const showDateRange = usesReportDateRange(queries);
  const computedRange = React.useMemo(
    () => (showDateRange
      ? computeReportDateRange(dateRange.preset, timeZone, dateRange.customFrom, dateRange.customTo)
      : null),
    [showDateRange, dateRange, timeZone]
  );

  // First time the query starts using the date tokens, seed the session's template
  // values with the default preset so fetches run the window shown.
  // Live mode always seeds: the definition carries the AUTHOR's instants from save
  // time, and the run must match the (possibly restored) range the bar displays.
  const seededRef = React.useRef(false);
  React.useEffect(() => {
    if (!showDateRange || seededRef.current) return;
    seededRef.current = true;
    if (!computedRange) return;
    const hasValues = !!safeString(templateValues?.[REPORT_DATE_FROM_TOKEN]).trim() &&
      !!safeString(templateValues?.[REPORT_DATE_TO_TOKEN]).trim();
    if (designMode && hasValues) return;
    onTemplateValuesChange?.({
      [REPORT_DATE_FROM_TOKEN]: computedRange.from.toISOString(),
      [REPORT_DATE_TO_TOKEN]: computedRange.to.toISOString(),
    });
    // Seeding is a one-shot on first token appearance, not a value-tracking effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDateRange]);

  const onDateRangeChange = (next) => {
    setDateRange(next);
    // Design mode: the author's last selection becomes the customization's default
    // (preset only). Live mode: the viewer's own selection is remembered.
    if (designMode) onSessionOptionsChange?.({ defaultDateRange: safeString(next.preset) });
    else if (stateKey) patchCustomizationPrefs(stateKey, { dateRange: next });
    const range = computeReportDateRange(next.preset, timeZone, next.customFrom, next.customTo);
    if (!range) return; // incomplete custom range — wait for both dates
    // The session refresh patches the template values, which re-triggers the page fetch.
    onRefresh?.({
      [REPORT_DATE_FROM_TOKEN]: range.from.toISOString(),
      [REPORT_DATE_TO_TOKEN]: range.to.toISOString(),
    });
  };

  // In-session memory write-through: cheap map set per relevant change, read back by
  // the initializers on the next mount within this SPA session. Gated on settingsReady:
  // writing the PRISTINE mount state would poison the map — a remount during the
  // durable restore's fetch window would read that empty entry as authoritative
  // session state, skip its own restore, and immediately save defaults over the
  // user's stored GridSettings row.
  React.useEffect(() => {
    if (!stateKey || !settingsReady) return;
    savedGridSessionState.set(stateKey, { skip, sort, filterDraft, quickFilters, appliedFilter, quickFilterSelection, dateRange, selectedRow, expandedRows: [...expandedRows] });
  }, [stateKey, settingsReady, skip, sort, filterDraft, quickFilters, appliedFilter, quickFilterSelection, dateRange, selectedRow, expandedRows]);

  // ── Saved quick filter actions (stateKey grids only) ──
  const toggleSavedQuickFilter = (id) => {
    setQuickFilterSelection((prev) => prev.map((v) => (v.ID === id ? { ...v, Selected: !v.Selected } : v)));
  };

  // "..." — pick which shared filters appear as pills (the standard grids' selector).
  const onPickQuickFilters = (e) => {
    showDialog(
      <RTLGridQuickFilterSelector
        filterSchemaName={scopeKey}
        selectedFilters={quickFilterSelection.map((v) => v.ID)}
      />
    ).then((s) => {
      if (!s.canceled) {
        setQuickFilterSelection((prev) => (s.selected ?? [])
          .map((id) => ({ ID: id, Selected: prev.find((v) => v.ID === id)?.Selected ?? false })));
      }
      e?.promise?.();
    });
  };

  // Saves the LOCAL filter (quick-row + builder) as a named, shareable quick filter.
  // Already-selected saved pills stay separate rather than nesting into the new one.
  const onSaveQuickFilter = (e) => {
    const tree = pruneFilter({
      logic: 'and',
      filters: [
        ...Object.entries(quickFilters)
          .map(([field, value]) => quickFilterDescriptor(field, value, columnFilterMeta[field]?.type))
          .filter(Boolean),
        filterDraft,
      ],
    });
    if (!tree) { e?.promise?.(); return; }
    showDialog(<GridFilterView customFormat filterSchemaName={scopeKey} filter={tree} />).then((r) => {
      if (!r.canceled && r.newID) {
        setSavedFilterEpoch((n) => n + 1); // the dialog cleared the cache; refetch defs
        setQuickFilterSelection((prev) => [...prev.filter((v) => v.ID !== r.newID), { ID: r.newID, Selected: true }]);
      }
      e?.promise?.();
    });
  };

  const quickFilterPills = (quickFilterSelection ?? [])
    .map((sel) => {
      const def = (savedFilterDefs ?? []).find((f) => f.ID === sel.ID);
      return def ? { ID: sel.ID, Name: def.Name, Selected: !!sel.Selected } : null;
    })
    .filter(Boolean);

  // ── Grid settings menu: reset + Excel export (mirrors the standard grid's
  // context menu actions) ──
  const [exporting, setExporting] = React.useState(false);
  const [exportNotice, setExportNotice] = React.useState('');

  // Back to the grid as authored: no sort, no filters, no pills, page 1. Also
  // wipes this instance's durable GridSettings row and session memory so the
  // reset survives navigation (the shared saved-filter DEFINITIONS are kept —
  // reset only deselects them here).
  const onResetGrid = async () => {
    if (stateKey) {
      try { await gridSettingsManager.resetGridSettings(stateKey); } catch { /* reset stays local-only */ }
      savedGridSessionState.delete(stateKey);
    }
    setSort(null);
    setFilterDraft(EMPTY_FILTER);
    setQuickFilters({});
    setQuickEditor(null);
    setAppliedFilter('');
    setQuickFilterSelection((prev) => prev.map((v) => ({ ...v, Selected: false })));
    setSelectedRow(null);
    setExpandedRows(new Set());
    setSkip(0);
    columnTypeCacheRef.current = {};
    setRefreshTick((n) => n + 1);
  };

  // Hands the export to the SERVER (the same background pipeline as custom data
  // exports): the workbook streams every row matching the viewer's current filter and
  // sort — not one grid page — with one tab per child grid, and lands in the
  // notification bell as a download. Values export raw (no picklist translation).
  const onExportExcel = async () => {
    if (!gridQuery || exporting || columns.length === 0) return;
    setExporting(true);
    setExportNotice('');
    try {
      const mapExportColumns = (cols) => (cols ?? [])
        .filter((c) => safeString(c?.field).trim())
        .map((c) => ({ field: c.field, title: c.title || c.field }));
      const mapExportChildGrids = (grids) => (grids ?? []).map((g) => ({
        title: safeString(g?.title).trim() || safeString(g?.queryName).trim() || safeString(g?.collection).trim(),
        queryName: safeString(g?.queryName).trim() || null,
        collection: safeString(g?.collection).trim() || null,
        columns: mapExportColumns(g?.columns),
        childGrids: mapExportChildGrids(g?.childGrids),
      }));

      const res = throwIfFrameworkException(await q_StartGridExport.getFirstOrDefault({
        apiVersion: safeString(apiVersion) || 'v2',
        label: safeString(exportLabel).trim() || t('Grid'),
        query: gridQuery,
        gridState: {
          sort: sort ? [{ field: sort.field, dir: sort.dir }] : [],
          filter: canFilter && appliedFilter ? JSON.parse(appliedFilter) : null,
        },
        templateValues: { ...(templateValues ?? {}) },
        columns: mapExportColumns(columns),
        childGrids: mapExportChildGrids(childGrids),
        queries: (queries ?? [])
          .filter((q) => safeString(q?.name).trim().toLowerCase() !== 'data' && safeString(q?.query).trim())
          .map((q) => ({ name: safeString(q.name).trim(), query: safeString(q.query) })),
      }));
      const ok = !!(res?.Ok ?? res?.ok);
      setExportNotice(ok
        ? t('Export started — you\u2019ll get a notification when it\u2019s ready to download.')
        : (safeString(res?.Error ?? res?.error) || t('The export could not be started.')));
    } catch (err) {
      setExportNotice(safeString(err?.message) || t('The export could not be started.'));
    } finally {
      setExporting(false);
    }
  };

  // ── Header sort: asc → desc → back to the query's own ORDER BY ──
  const onHeaderClick = (column) => {
    if (column.sortable === false) return;
    setSkip(0);
    setSelectedRow(null);
    setExpandedRows(new Set());
    setSort((prev) => {
      if (prev?.field !== column.field) return { field: column.field, dir: 'asc' };
      return prev.dir === 'asc' ? { field: column.field, dir: 'desc' } : null;
    });
  };

  const toggleExpanded = (ix) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(ix)) next.delete(ix); else next.add(ix);
      return next;
    });
  };

  // ── Pager ──
  const { rows, total, loading, error } = fetchState;
  const pageNumber = Math.floor(skip / GRID_PAGE_SIZE) + 1;
  const pageCount = total != null ? Math.max(1, Math.ceil(total / GRID_PAGE_SIZE)) : null;
  const atEnd = total != null ? skip + GRID_PAGE_SIZE >= total : rows.length < GRID_PAGE_SIZE;
  const columnSpan = columns.length + (hasExpander ? 1 : 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minHeight: 0 }}>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <GridToolbarButton
          icon="refresh"
          label={t('Refresh')}
          tagName="gridRefresh"
          disabled={busy || loading}
          onClick={() => { setSkip(0); setRefreshTick((n) => n + 1); onRefresh?.(); }}
        />
        {canFilter && columns.length > 0 && (
          <GridToolbarButton
            icon="filter_list"
            label={`${t('Filter')}${activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}`}
            tagName="toggleGridFilter"
            active={showFilter}
            disabled={busy}
            onClick={() => setShowFilter((v) => !v)}
          />
        )}
        {showDateRange && (
          <ReportDateRangePicker
            value={dateRange}
            disabled={busy || loading}
            timeZone={timeZone}
            locale={locale}
            onChange={onDateRangeChange}
          />
        )}
        <div style={{ flex: 1 }} />
        {appliedFilter && (
          <button
            type="button"
            data-tagname="clearGridFilter"
            style={{ border: 'none', background: 'none', fontSize: 12, color: '#6f42c1', cursor: 'pointer' }}
            onClick={() => {
              setFilterDraft(EMPTY_FILTER);
              setQuickFilters({});
              setQuickEditor(null);
              setAppliedFilter('');
              setSkip(0);
              setExpandedRows(new Set());
              // Deselect (but keep) the saved quick filter pills too — "clear" means
              // everything currently narrowing the grid.
              setQuickFilterSelection((prev) => prev.map((v) => ({ ...v, Selected: false })));
            }}
          >
            {t('Clear filter')} ({activeFilterCount})
          </button>
        )}
        {sort && (
          <button
            type="button"
            data-tagname="clearSort"
            style={{ border: 'none', background: 'none', fontSize: 12, color: '#6f42c1', cursor: 'pointer' }}
            onClick={() => { setSkip(0); setSelectedRow(null); setExpandedRows(new Set()); setSort(null); }}
          >
            {t('Clear sort')} ({columns.find((c) => c.field === sort.field)?.title ?? sort.field} {sort.dir})
          </button>
        )}
        {gridQuery && columns.length > 0 && (
          <MoreButton
            disabled={busy || loading || exporting}
            actions={[
              { id: 'resetGrid', text: t('Reset Grid'), onClick: () => { onResetGrid(); } },
              { id: 'exportExcel', text: exporting ? t('Starting export...') : t('Export to Excel'), onClick: () => { onExportExcel(); } },
            ]}
          />
        )}
      </div>

      {exportNotice && (
        <div
          data-tagname="gridExportNotice"
          style={{ fontSize: 12, padding: '6px 10px', borderRadius: 4, background: '#eef7f0', border: '1px solid #cfe8d6', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <span style={{ flex: 1 }}>{exportNotice}</span>
          <button
            type="button"
            style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, opacity: 0.6 }}
            onClick={() => setExportNotice('')}
          >
            {'\u2715'}
          </button>
        </div>
      )}

      {/* ── Saved quick filter pills (shared GridFilter rows scoped to this grid) ── */}
      {stateKey && savedFilterDefs !== null && (quickFilterPills.length > 0 || savedFilterDefs.length > 0) && (
        <div data-tagname="customGridQuickFilters" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700 }}>{t('Quick filters')}</span>
          {quickFilterPills.map((pill) => (
            <button
              key={pill.ID}
              type="button"
              data-tagname={`customQuickFilter_${pill.ID}`}
              style={quickFilterPillStyle(pill.Selected)}
              onClick={() => toggleSavedQuickFilter(pill.ID)}
            >
              {pill.Name}
            </button>
          ))}
          <AccentButton size="sm" outline tagName="pickQuickFilters" onClick={onPickQuickFilters} tooltip={t('Choose which saved quick filters appear here.')}>
            ...
          </AccentButton>
        </div>
      )}

      {/* ── Viewer filter panel (KendoReact Filter; mapped to the structured filter tree) ── */}
      {showFilter && canFilter && columns.length > 0 && (
        <div data-tagname="gridFilterPanel" style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: 8, background: '#fff' }}>
          <Filter
            value={filterDraft}
            onChange={(e) => setFilterDraft(e.filter)}
            fields={filterFields}
          />
          {stateKey && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
              <AccentButton
                size="sm"
                outline
                tagName="saveQuickFilter"
                disabled={busy || countFilterConditions(pruneFilter(filterDraft)) + Object.keys(quickFilters).length === 0}
                onClick={onSaveQuickFilter}
                tooltip={t('Save the current filter as a named quick filter you can share with users or teams.')}
              >
                {t('Save as quick filter...')}
              </AccentButton>
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ border: '1px solid #ffcdd2', background: '#ffebee', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#b71c1c' }}>
          {error}
        </div>
      )}
      {definitionError && (
        <div style={{ border: '1px solid #ffe0b2', background: '#fff8e1', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#8a6d3b' }}>
          {definitionError}
        </div>
      )}

      {/* ── The grid ── */}
      <div style={{ position: 'relative', flex: 1, minHeight: 0, border: '1px solid #dee2e6', borderRadius: 6, overflow: 'auto', background: '#fff' }}>
        {columns.length > 0 ? (
          <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%' }}>
            <thead>
              {/* ── Headers: title + sort, with the quick-filter funnel inline (clicking
                     the funnel zooms into the overlay editor without touching the sort) ── */}
              <tr>
                {hasExpander && <th style={{ ...headerCellStyle(false), width: 28 }} />}
                {columns.map((column) => {
                  const meta = columnFilterMeta[column.field];
                  const isDate = meta?.type === 'date';
                  const summary = quickFilterSummary(quickFilters[column.field], meta);
                  const active = !!summary;
                  return (
                    <th
                      key={column.field}
                      ref={(el) => { quickCellRefs.current[column.field] = el; }}
                      data-tagname={`gridHeader_${column.field}`}
                      style={{ ...headerCellStyle(column.sortable !== false), width: column.width, ...(column.align ? { textAlign: column.align } : null) }}
                      title={column.sortable === false ? column.field : `${column.field} — ${t('click to sort')}`}
                      onClick={() => onHeaderClick(column)}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%', verticalAlign: 'middle' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{column.title}</span>
                        <SortIndicator dir={sort?.field === column.field ? sort.dir : null} />
                        {canFilter && (
                          <button
                            type="button"
                            data-role="quickFilterStub"
                            data-tagname={`quickFilter_${column.field}`}
                            disabled={isDate}
                            title={isDate
                              ? t('Use the Filter panel for date ranges.')
                              : (active ? `${t('Filter')}: ${summary}` : `${t('Filter')} ${column.title}`)}
                            onClick={(e) => { e.stopPropagation(); openQuickEditor(column.field); }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 3,
                              marginLeft: 5,
                              minWidth: 0,
                              border: `1px solid ${active ? '#b197d8' : 'transparent'}`,
                              background: active ? '#f3edfb' : 'transparent',
                              color: active ? '#5a32a3' : '#9aa0a6',
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 400,
                              padding: '1px 4px',
                              cursor: isDate ? 'default' : 'pointer',
                              opacity: isDate ? 0.35 : 1,
                            }}
                          >
                            <FunnelIcon size={9} />
                            {active && (
                              <span style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
                            )}
                          </button>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ix) => {
                const isSelected = selectedRow === skip + ix;
                return (
                // Page rows have no stable identity; index keys are fine for a read-only page.
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={ix}>
                  <tr
                    onClick={(e) => {
                      // A click that lands on a link (the A component tags itself with
                      // data-linkid) SELECTS the row it navigates from — toggling here
                      // would clear the selection an instant before leaving the page.
                      const isLinkClick = !!e.target?.closest?.('[data-linkid]');
                      setSelectedRow((prev) => (isLinkClick || prev !== skip + ix ? skip + ix : null));
                    }}
                    style={{
                      cursor: 'pointer',
                      ...(definition?.rowHeight ? { height: definition.rowHeight } : null),
                      // Declared row styling first; the selection tint composes AFTER
                      // it so a selected zebra row still reads as selected.
                      ...rowStyleFor(definition, row, skip + ix),
                      ...(isSelected ? { background: '#eef4fd' } : null),
                      // Inset bar reads as "selected" even where custom cell styles
                      // paint their own backgrounds over the row tint.
                      boxShadow: isSelected ? 'inset 3px 0 0 #4c8dd6' : undefined,
                    }}
                  >
                    {hasExpander && (
                      <td style={{ ...bodyCellStyle, cursor: 'pointer', textAlign: 'center', width: 28 }} onClick={(e) => { e.stopPropagation(); toggleExpanded(skip + ix); }}>
                        <span style={{ display: 'inline-block', transform: expandedRows.has(skip + ix) ? 'rotate(90deg)' : 'none', transition: 'transform 0.1s', fontSize: 10, opacity: 0.7 }}>
                          {'\u25B6'}
                        </span>
                      </td>
                    )}
                    {columns.map((column) => (
                      <td key={column.field} style={cellStyleFor(row, column, bodyCellStyle)}>
                        {renderCellContent(row, column, locale)}
                      </td>
                    ))}
                  </tr>
                  {hasExpander && expandedRows.has(skip + ix) && (
                    <tr>
                      <td colSpan={columnSpan} style={{ ...bodyCellStyle, padding: '4px 10px 8px 34px' }}>
                        <ChildGridsBlock childGrids={childGrids} parentRow={row} ctx={childGridContext} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })}
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={columnSpan} style={{ padding: 16, fontSize: 13, opacity: 0.7 }}>
                    {t('The query matched no rows with the current filters/date range.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        ) : definitionPending ? (
          <div style={{ padding: 16, fontSize: 13, opacity: 0.7, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AccentSpinner size="sm" /> {t('Preparing the grid...')}
          </div>
        ) : (
          <div style={{ padding: 16, fontSize: 13, opacity: 0.7 }}>
            {gridQuery
              ? t('No rows to display yet.')
              : t('No grid yet — describe the data you want to browse in the conversation (e.g. "all open jobs with customer and amount, with their job lines").')}
          </div>
        )}
        {quickEditor && (() => {
          const column = columns.find((c) => c.field === quickEditor.field);
          if (!column) return null;
          return (
            <QuickFilterOverlay
              ref={quickOverlayRef}
              column={column}
              meta={columnFilterMeta[quickEditor.field]}
              value={safeString(quickFilters[quickEditor.field])}
              position={quickEditor}
              onChange={(value) => setQuickFilterValue(quickEditor.field, value)}
              onClose={() => setQuickEditor(null)}
            />
          );
        })()}
        {(busy || loading) && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.6)',
            zIndex: 2,
          }}>
            <AccentSpinner />
          </div>
        )}
      </div>

      {/* ── Pager + row facts ── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
        <GridToolbarButton
          icon="chevron_left"
          label={t('Previous')}
          tagName="gridPagePrev"
          disabled={busy || loading || skip === 0}
          onClick={() => setSkip(Math.max(0, skip - GRID_PAGE_SIZE))}
        />
        <GridToolbarButton
          icon="chevron_right"
          label={t('Next')}
          tagName="gridPageNext"
          disabled={busy || loading || atEnd}
          onClick={() => setSkip(skip + GRID_PAGE_SIZE)}
        />
        <span style={{ opacity: 0.75 }}>
          {total != null
            ? `${t('Page')} ${pageNumber}${pageCount != null ? ` ${t('of')} ${pageCount}` : ''} (${total.toLocaleString(locale)} ${t('rows')})`
            : rows.length > 0 ? `${t('Page')} ${pageNumber}` : ''}
        </span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, opacity: 0.65 }}>
          {gridQuery && (semanticTop != null
            ? `${t('This grid shows at most')} ${semanticTop.toLocaleString(locale)} ${t('rows (TOP is the grid\u2019s row cap).')}`
            : t('Sort by clicking a column header; the grid pages through every matching row.'))}
        </span>
      </div>
    </div>
  );
};
